import { createFileRoute, useMatch } from '@tanstack/react-router'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { useLingui } from '@lingui/react/macro'
import { ApplicantIcon, JobIcon } from '@/routes/_app/-components/settings/settings-icons'
import { formatDateLocale, formatNumber } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { SandboxResetHint, UnlinkApplicantAction } from './-components/api/entity-row-actions'
import { IntegrationListPanel, IntegrationRow, MetaText } from './-components/api/integration-list'
import { Avatar } from './-components/shared/avatar'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { IntegrationListPending } from './-components/settings/account-pending'

export const Route = createFileRoute('/_app/account/integrations/$appId/applicants')({
  component: RouteComponent,
  pendingComponent: IntegrationListPending,
  // Painted on the click frame; safe only because the searched list key lives on the pathless `_list` layout below.
})

function RouteComponent() {
  const { language } = useTranslation()
  const { t, i18n } = useLingui()
  const { q } = useRootSearch()
  const { appId } = Route.useParams()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: q })

  const { context } = useMatch({ from: '/_app/account/integrations/$appId/applicants/_list' })
  // Defer the OPTIONS object (the context is reference-stable per match), so a new search keeps the
  // previous page on screen while the next one loads instead of flashing the skeleton.
  const deferredOptions = useDeferredValue(context.appApplicantsQueryOptions)
  const { data: applicants, isFetching } = useSuspenseQuery(deferredOptions)
  // True while the deferred options lag the live ones — keeps the dimming through that transition.
  const isStale = isFetching || deferredOptions !== context.appApplicantsQueryOptions

  const total = applicants.pagination.count
  // Plural chosen on the number, shown formatted.
  const count = formatNumber(i18n.locale, total, 0)
  return (
    <div className={SETTINGS_STACK}>
      <SandboxResetHint />
      <IntegrationListPanel
        entity="applicants"
        query={searchInput}
        onQueryChange={handleSearchChange}
        count={total === 1 ? t`${count} candidate` : t`${count} candidates`}
        isStale={isStale}
        isEmpty={applicants.list.length === 0}
        empty={{
          noResults: Boolean(q),
          icon: <ApplicantIcon className="text-muted-foreground size-5" />,
          title: t`No candidates yet`,
          description: t`Candidates your integration creates or links through the API show here.`,
        }}
        pagination={applicants.pagination}
      >
        {applicants.list.map((elem) => {
          const name = elem.name ?? elem.email ?? t`Unnamed`
          const jobs = elem.jobs
          return (
            <IntegrationRow
              key={elem.id}
              media={<Avatar size="md" type="applicant" name={name} forceInitials />}
              title={name}
              meta={
                <>
                  <CompletionIndicator
                    skills={elem.testPassed}
                    values={elem.testPassedValues}
                    traits={elem.testPassedPersonality}
                  />
                  <span className="flex shrink-0 items-center gap-1" title={t`Jobs`}>
                    <JobIcon className="size-3" />
                    {formatNumber(i18n.locale, jobs, 0)}
                  </span>
                  {elem.email && elem.name && <MetaText>{elem.email}</MetaText>}
                  {elem.created && (
                    <span className="hidden shrink-0 sm:inline">{formatDateLocale(elem.created, language)}</span>
                  )}
                </>
              }
              actions={<UnlinkApplicantAction appClientId={appId} applicantId={elem.id} name={name} />}
            />
          )
        })}
      </IntegrationListPanel>
    </div>
  )
}
